// src/components/ui/OvylonSelect.tsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

type Option = {
  value: string;
  label: string;
  disabled?: boolean;
  hint?: string;
};

type Props = {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  menuClassName?: string;
  /** si quieres ejecutar lógica especial cuando se elige cierto value */
  onSelectValue?: (value: string) => void;
};

type MenuPos = {
  left: number;
  width: number;
  top?: number; // cuando abre abajo
  bottom?: number; // cuando abre arriba
  maxHeight: number;
  openUp: boolean;
};

export default function OvylonSelect({
  label,
  value,
  onChange,
  options,
  placeholder = "Seleccionar…",
  disabled,
  className = "",
  menuClassName = "",
  onSelectValue,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  const computeMenuPos = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const gap = 8; // similar a mt-2
    const viewportH = window.innerHeight;

    const spaceBelow = viewportH - rect.bottom - gap;
    const spaceAbove = rect.top - gap;

    // 72 * 4 = 288px (max-h-72)
    const hardMax = 288;
    const minComfort = 180;

    const openUp = spaceBelow < minComfort && spaceAbove > spaceBelow;

    const available = Math.max(120, (openUp ? spaceAbove : spaceBelow) - 8);
    const maxHeight = Math.min(hardMax, available);

    // clamp within viewport on X
    const leftRaw = rect.left;
    const width = rect.width;
    const left = Math.max(8, Math.min(leftRaw, window.innerWidth - width - 8));

    if (openUp) {
      // pegado al botón desde arriba: bottom = distancia desde bottom del viewport
      setMenuPos({
        left,
        width,
        bottom: Math.max(8, viewportH - rect.top + gap),
        maxHeight,
        openUp: true,
      });
    } else {
      setMenuPos({
        left,
        width,
        top: Math.max(8, rect.bottom + gap),
        maxHeight,
        openUp: false,
      });
    }
  }, []);

  // close on outside click (considerando portal)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;

      const root = rootRef.current;
      const menu = menuRef.current;

      const insideRoot = !!root && root.contains(t);
      const insideMenu = !!menu && menu.contains(t);

      if (!insideRoot && !insideMenu) setOpen(false);
    };

    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // cuando abre, calcula posición y recalcula en scroll/resize
  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }

    computeMenuPos();

    const onRecalc = () => computeMenuPos();
    window.addEventListener("resize", onRecalc);
    window.addEventListener("scroll", onRecalc, true); // captura scroll en contenedores

    return () => {
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc, true);
    };
  }, [open, computeMenuPos]);

  const triggerClass =
    "w-full h-[46px] rounded-xl px-4 pr-10 text-sm text-left " +
    "bg-white/5 hover:bg-white/10 " +
    "border border-white/10 hover:border-white/20 " +
    "text-gray-200 outline-none transition " +
    "focus-visible:ring-2 focus-visible:ring-indigo-500/40 " +
    (disabled ? "opacity-55 cursor-not-allowed " : "cursor-pointer ") +
    className;

  const menuNode =
    open && !disabled && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={{
              position: "fixed",
              left: menuPos.left,
              width: menuPos.width,
              top: menuPos.openUp ? undefined : menuPos.top,
              bottom: menuPos.openUp ? menuPos.bottom : undefined,
              zIndex: 9999,
            }}
            className={
              "overflow-hidden rounded-2xl " +
              "border border-white/10 bg-[#0b0b13]/95 backdrop-blur-xl " +
              "shadow-[0_24px_120px_-40px_rgba(0,0,0,0.9)] " +
              menuClassName
            }
          >
            <div className="overflow-auto py-1" style={{ maxHeight: menuPos.maxHeight }}>
              {options.map((opt) => {
                const isSelected = opt.value === value;
                const isDisabled = !!opt.disabled;

                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;

                      onSelectValue?.(opt.value);

                      onChange(opt.value);
                      setOpen(false);
                      btnRef.current?.focus();
                    }}
                    className={
                      "w-full px-3 py-2.5 text-left text-sm flex items-center justify-between gap-3 " +
                      "transition " +
                      (isDisabled ? "opacity-50 cursor-not-allowed " : "cursor-pointer ") +
                      (isSelected ? "bg-indigo-500/15 text-white " : "text-gray-200 hover:bg-white/8")
                    }
                  >
                    <span className="truncate">{opt.label}</span>

                    {isSelected && (
                      <span className="text-indigo-300">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path
                            d="M20 6L9 17l-5-5"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="w-full" ref={rootRef}>
      {label && <label className="block text-xs text-gray-400 mb-1">{label}</label>}

      <div className="relative">
        <button
          ref={btnRef}
          type="button"
          disabled={disabled}
          className={triggerClass}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => {
            if (disabled) return;
            setOpen((v) => !v);
          }}
        >
          {/* ✅ Selected en una sola línea: label izquierda, hint derecha */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-between gap-3 w-full min-w-0">
              <span className="truncate">
                {selected?.label || (value ? value : "") || placeholder}
              </span>

              {selected?.hint && (
                <span className="shrink-0 text-[11px] text-gray-500">
                  {selected.hint}
                </span>
              )}
            </div>
          </div>

          {/* Chevron */}
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/60">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {menuNode}
      </div>
    </div>
  );
}

